import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface BusinessIdea {
  id: string;
  user_id: string;
  title: string;
  description: string;
  industry: string;
  target_market: string;
  revenue_model: string;
  details: {
    products?: Array<{
      name: string;
      description: string;
      estimatedCost?: number;
    }>;
    services?: Array<{
      name: string;
      description: string;
      estimatedPrice?: number;
    }>;
    requiredSkills: Array<{
      name: string;
      userLevel?: string;
      skillGap?: 'none' | 'missing';
    }>;
    marketScope?: string;
    timeCommitment?: string;
    workEnvironment?: string;
  };
  created_at: string;
  updated_at: string;
}

export const useIdeas = () => {
  const { user } = useAuth();
  const [ideas, setIdeas] = useState<BusinessIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadIdeas();
    }
  }, [user]);

  const loadIdeas = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('ideas')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setIdeas(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  const createIdea = async (idea: Omit<BusinessIdea, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    try {
      if (!user) throw new Error('User must be logged in to create ideas');

      const { data, error } = await supabase
        .from('ideas')
        .insert([
          {
            ...idea,
            user_id: user.id,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      setIdeas((prev) => [data, ...prev]);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create idea');
      throw err;
    }
  };

  const updateIdea = async (id: string, updates: Partial<BusinessIdea>) => {
    try {
      if (!user) throw new Error('User must be logged in to update ideas');

      const { data, error } = await supabase
        .from('ideas')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      setIdeas((prev) => prev.map((idea) => (idea.id === id ? data : idea)));
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update idea');
      throw err;
    }
  };

  const deleteIdea = async (id: string) => {
    try {
      if (!user) throw new Error('User must be logged in to delete ideas');

      const { error } = await supabase
        .from('ideas')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      setIdeas((prev) => prev.filter((idea) => idea.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete idea');
      throw err;
    }
  };

  // Subscribe to real-time changes
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('ideas_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ideas',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setIdeas((prev) => [payload.new as BusinessIdea, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setIdeas((prev) =>
              prev.map((idea) =>
                idea.id === payload.new.id ? (payload.new as BusinessIdea) : idea
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setIdeas((prev) =>
              prev.filter((idea) => idea.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  return {
    ideas,
    loading,
    error,
    createIdea,
    updateIdea,
    deleteIdea,
    refreshIdeas: loadIdeas,
  };
};
